import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  type CalibreFamilyEntry,
  type CustomCalibreEntry,
  addCalibreToFamily,
  getCalibreFamiliesForManufacturer,
  getCalibreFamilyById,
  getCalibresForManufacturer,
  removeCalibreFromFamily,
} from '../../services/firebase';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import { Badge } from '../common/Badge';

/**
 * Family assignment control for the calibre editor — the mirror of the family
 * editor's member picker. Lets an admin place this (persisted) calibre into one
 * of its manufacturer's families, optionally flag it as the family's base, and
 * later change or remove that membership.
 *
 * Membership is tracked by `familyId` on the calibre doc (families have no
 * `memberIds` array — the family editor derives members by querying calibres),
 * so writes go straight to the calibre doc via {@link addCalibreToFamily} /
 * {@link removeCalibreFromFamily} and happen immediately, independent of the
 * calibre form's own Save. `onFamilyChange` lets the parent keep its copy of the
 * calibre's `familyId` in sync after each write.
 *
 * Only meaningful for a persisted calibre with a primary manufacturer (families
 * are per-manufacturer); the parent gates rendering on `calibreId`, and this
 * component shows a hint when no manufacturer is set.
 */
export function CalibreFamilyField({
  calibreId,
  manufacturerId,
  familyId,
  onFamilyChange,
}: {
  calibreId: string;
  manufacturerId: string;
  familyId: string | null;
  onFamilyChange: (familyId: string | null) => void;
}) {
  const [family, setFamily] = useState<CalibreFamilyEntry | null>(null);
  const [resolving, setResolving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [removing, setRemoving] = useState(false);

  // Resolve the current family doc for display whenever the familyId changes.
  useEffect(() => {
    let cancelled = false;
    if (!familyId) {
      setFamily(null);
      return;
    }
    setResolving(true);
    getCalibreFamilyById(familyId)
      .then((f) => {
        if (!cancelled) setFamily(f);
      })
      .catch((err) => {
        console.error('[CalibreFamilyField] resolve family failed', err);
        if (!cancelled) setFamily(null);
      })
      .finally(() => {
        if (!cancelled) setResolving(false);
      });
    return () => {
      cancelled = true;
    };
  }, [familyId]);

  const isBase = !!family && !!calibreId && family.baseCalibreId === calibreId;

  const handleRemove = async () => {
    if (!familyId) return;
    const label = family?.displayName || family?.name || 'this family';
    if (
      !window.confirm(
        `Remove this calibre from "${label}"?${
          isBase ? '\n\nIt is currently the family’s base calibre — that will be cleared too.' : ''
        }`,
      )
    ) {
      return;
    }
    setRemoving(true);
    try {
      await removeCalibreFromFamily(calibreId, familyId);
      onFamilyChange(null);
      toast.success('Removed from family');
    } catch (err) {
      console.error('[CalibreFamilyField] remove failed', err);
      toast.error(`Failed to remove from family: ${errorMessage(err)}`);
    } finally {
      setRemoving(false);
    }
  };

  const handleAdded = (pickedFamilyId: string) => {
    onFamilyChange(pickedFamilyId);
    setDialogOpen(false);
  };

  return (
    <div className="col-span-2">
      <label className="block text-xs font-medium text-gray-600 mb-1">
        Movement Family
      </label>

      {!manufacturerId ? (
        <p className="text-xs text-gray-400 italic">
          Set a primary manufacturer to assign this calibre to a family.
        </p>
      ) : familyId ? (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-sm font-medium bg-brand-50 text-brand-800 border border-brand-200">
            {resolving && !family
              ? 'Loading…'
              : family?.displayName || family?.name || familyId}
            {isBase && <Badge variant="info">base</Badge>}
          </span>
          <button
            type="button"
            onClick={() => setDialogOpen(true)}
            className="text-xs text-brand-600 hover:text-brand-800 underline"
          >
            Change
          </button>
          <button
            type="button"
            onClick={handleRemove}
            disabled={removing}
            className="text-xs text-red-600 hover:text-red-800 underline disabled:opacity-50"
          >
            {removing ? 'Removing…' : 'Remove from family'}
          </button>
        </div>
      ) : (
        <Button variant="secondary" size="sm" onClick={() => setDialogOpen(true)}>
          + Add to Family
        </Button>
      )}
      <p className="mt-1 text-xs text-gray-400">
        Groups this calibre under a movement family (e.g. Sellita SW200). Families
        are managed on the manufacturer’s detail screen.
      </p>

      {dialogOpen && (
        <AddToFamilyDialog
          calibreId={calibreId}
          manufacturerId={manufacturerId}
          currentFamilyId={familyId}
          onClose={() => setDialogOpen(false)}
          onAdded={handleAdded}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Add / change family dialog
// ---------------------------------------------------------------------------

function AddToFamilyDialog({
  calibreId,
  manufacturerId,
  currentFamilyId,
  onClose,
  onAdded,
}: {
  calibreId: string;
  manufacturerId: string;
  currentFamilyId: string | null;
  onClose: () => void;
  onAdded: (familyId: string) => void;
}) {
  // Families + this manufacturer's calibres are loaded together so we can show a
  // member count and resolved base-calibre name per row (mirrors the manufacturer
  // detail's Families panel). Firestore already scopes families to the
  // manufacturer, which is exactly the required "same manufacturer" filter.
  const [families, setFamilies] = useState<CalibreFamilyEntry[]>([]);
  const [calibres, setCalibres] = useState<CustomCalibreEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [setAsBase, setSetAsBase] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      getCalibreFamiliesForManufacturer(manufacturerId),
      getCalibresForManufacturer(manufacturerId),
    ])
      .then(([fams, cals]) => {
        if (cancelled) return;
        setFamilies(fams);
        setCalibres(cals);
      })
      .catch((err) => {
        console.error('[AddToFamilyDialog] load failed', err);
        if (!cancelled) toast.error(`Failed to load families: ${errorMessage(err)}`);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [manufacturerId]);

  const memberCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const c of calibres) {
      if (c.familyId) counts.set(c.familyId, (counts.get(c.familyId) ?? 0) + 1);
    }
    return counts;
  }, [calibres]);

  const calibreNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of calibres) m.set(c.id, c.name);
    return m;
  }, [calibres]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return families;
    return families.filter(
      (f) =>
        f.name.toLowerCase().includes(q) ||
        (f.displayName ?? '').toLowerCase().includes(q),
    );
  }, [families, search]);

  const selectedFamily = useMemo(
    () => families.find((f) => f.id === selectedId) ?? null,
    [families, selectedId],
  );
  // The base checkbox only matters when the picked family already has a
  // different base calibre — warn that submitting will replace it.
  const baseWillReplace =
    setAsBase &&
    !!selectedFamily?.baseCalibreId &&
    selectedFamily.baseCalibreId !== calibreId;

  const handleSubmit = async () => {
    if (!selectedId) return;
    setSubmitting(true);
    try {
      await addCalibreToFamily(calibreId, selectedId, { asBase: setAsBase });
      toast.success(
        setAsBase ? 'Added to family and set as base' : 'Added to family',
      );
      onAdded(selectedId);
    } catch (err) {
      console.error('[AddToFamilyDialog] submit failed', err);
      toast.error(`Failed to add to family: ${errorMessage(err)}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Add calibre to family" size="lg">
      <div className="space-y-3">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          autoFocus
          placeholder="Search this manufacturer’s families…"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
        />

        <div className="border border-gray-200 rounded-lg max-h-72 overflow-y-auto">
          {loading ? (
            <div className="text-sm text-gray-500 p-3">Loading families…</div>
          ) : filtered.length === 0 ? (
            <div className="text-sm text-gray-500 italic p-3">
              {families.length === 0
                ? 'No families for this manufacturer yet. Create one on the manufacturer’s detail screen.'
                : 'No families match your search.'}
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {filtered.map((f) => {
                const count = memberCounts.get(f.id) ?? 0;
                const baseName = f.baseCalibreId
                  ? calibreNameById.get(f.baseCalibreId) ?? f.baseCalibreId
                  : null;
                const isCurrent = f.id === currentFamilyId;
                const isSelected = f.id === selectedId;
                return (
                  <li key={f.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedId(f.id)}
                      className={`w-full text-left px-3 py-2 transition-colors ${
                        isSelected ? 'bg-brand-50' : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-2 flex-wrap">
                        <input
                          type="radio"
                          checked={isSelected}
                          readOnly
                          className="text-brand-600 focus:ring-brand-500"
                        />
                        <span className="font-medium text-gray-900">{f.name}</span>
                        {f.displayName && f.displayName !== f.name && (
                          <span className="text-xs text-gray-400">
                            ({f.displayName})
                          </span>
                        )}
                        <Badge variant={count > 0 ? 'info' : 'default'}>
                          {count} member{count === 1 ? '' : 's'}
                        </Badge>
                        {baseName && <Badge variant="default">base: {baseName}</Badge>}
                        {isCurrent && <Badge variant="success">current</Badge>}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Base checkbox — only shown once a family is picked. */}
        {selectedFamily && (
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={setAsBase}
                onChange={(e) => setSetAsBase(e.target.checked)}
                className="mt-0.5 rounded text-brand-600 focus:ring-brand-500"
              />
              <span className="text-sm text-gray-700">
                Set as base calibre for this family
              </span>
            </label>
            {baseWillReplace && (
              <p className="mt-1 ml-6 text-xs text-amber-700">
                ⚠️ This will replace the family’s current base calibre (
                {calibreNameById.get(selectedFamily.baseCalibreId ?? '') ??
                  selectedFamily.baseCalibreId}
                ).
              </p>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t">
          <Button variant="secondary" size="sm" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSubmit}
            disabled={!selectedId || submitting}
            loading={submitting}
          >
            {setAsBase ? 'Add and set as base' : 'Add to family'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * Best-effort human-readable cause from a thrown value — surfaces a Firestore
 * `code` (e.g. `permission-denied`) alongside its message so a toast names the
 * real backend cause instead of a generic failure string.
 */
function errorMessage(err: unknown): string {
  if (err instanceof Error) {
    const code = (err as { code?: string }).code;
    return code && !err.message.includes(code) ? `${err.message} (${code})` : err.message;
  }
  if (typeof err === 'string') return err;
  return 'Unknown error';
}
